import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Easing, LayoutChangeEvent, Platform, Pressable, StyleSheet, View } from "react-native";
import { ChevronRight } from "lucide-react";
import { AnimatePresence, MotiView } from "moti";
import confetti from "canvas-confetti";
import AxioButton from "../../axio-ds/Button/AxioButton";
import CustomText from "../../common/general/CustomText/CustomText";
import { colors, radii, shadowStyles, spacing, typographyScale } from "../../../styles/design-system";
import type { Objective } from "../../../containers/companion/LearningGoalsContainer";
import completeSoundSrc from "../../../assets/audio/general/complete.mp3";

interface ObjectivesProgressBarProps {
  objectives: Objective[];
  onMoveOn: () => void;
  isStreaming?: boolean;
  hasPendingQuestion?: boolean;
}

const getObjectiveKey = (objective: Objective): string => objective.id ?? objective.objective;

const getActiveObjective = (list: Objective[]): Objective | null => {
  if (!list.length) return null;
  const firstIncomplete = list.find((o) => !o.isCompleted);
  return firstIncomplete ?? list[list.length - 1];
};

const PANEL_ENTER_MS = 220;
const PANEL_EXIT_MS = 160;

const ObjectivesProgressBar: React.FC<ObjectivesProgressBarProps> = ({
  objectives,
  onMoveOn,
  isStreaming = false,
  hasPendingQuestion = false,
}) => {
  const { t } = useTranslation();
  const wrapperRef = useRef<View | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [barHovered, setBarHovered] = useState(false);
  const [barHeight, setBarHeight] = useState<number>(spacing["3xl"]);
  const prevCompletedRef = useRef<Set<string>>(new Set(objectives.filter((o) => o.isCompleted).map(getObjectiveKey)));
  const [pulsingKeys, setPulsingKeys] = useState<Set<string>>(new Set());
  const pulseClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const activeObjective = getActiveObjective(objectives);
  const completedCount = objectives.filter((o) => o.isCompleted).length;
  const allComplete = objectives.length > 0 && completedCount === objectives.length;

  useEffect(() => {
    if (!isExpanded || Platform.OS !== "web") {
      return;
    }
    const onPointerDown = (e: MouseEvent) => {
      const root = wrapperRef.current as unknown as HTMLElement | null;
      if (root && typeof root.contains === "function" && !root.contains(e.target as Node)) {
        setIsExpanded(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [isExpanded]);

  useEffect(() => {
    const currentCompleted = new Set(objectives.filter((o) => o.isCompleted).map(getObjectiveKey));
    const newlyCompleted = [...currentCompleted].filter((key) => !prevCompletedRef.current.has(key));

    if (newlyCompleted.length > 0) {
      setPulsingKeys(new Set(newlyCompleted));
      if (pulseClearRef.current) {
        clearTimeout(pulseClearRef.current);
      }
      pulseClearRef.current = setTimeout(() => {
        setPulsingKeys(new Set());
        pulseClearRef.current = null;
      }, 400);

      if (Platform.OS === "web") {
        if (!audioRef.current) {
          audioRef.current = new Audio(completeSoundSrc);
          audioRef.current.volume = 0.4;
        }
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
        confetti({
          particleCount: 28,
          spread: 58,
          origin: { x: 0.5, y: 0.88 },
          colors: [colors.accent.purple500, colors.brand[400], colors.brand[200]],
          startVelocity: 18,
          gravity: 0.85,
          ticks: 95,
          scalar: 0.75,
        });
      }
    }

    prevCompletedRef.current = currentCompleted;
  }, [objectives]);

  useEffect(() => {
    return () => {
      if (pulseClearRef.current) {
        clearTimeout(pulseClearRef.current);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  if (!objectives.length) return null;

  return (
    <View ref={wrapperRef} style={styles.wrapper}>
      <AnimatePresence>
        {isExpanded ? (
          <MotiView
            key="objectives-expanded-panel"
            style={[styles.expandedPanel, shadowStyles.md, { bottom: barHeight }]}
            from={{ opacity: 0, translateY: spacing.xl }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: spacing.xl }}
            transition={{
              type: "timing",
              duration: PANEL_ENTER_MS,
              easing: Easing.out(Easing.cubic),
            }}
            exitTransition={{
              type: "timing",
              duration: PANEL_EXIT_MS,
              easing: Easing.in(Easing.cubic),
            }}>
            {objectives.map((obj, idx) => {
              const key = getObjectiveKey(obj);
              const completed = !!obj.isCompleted;
              const isActive = !completed && activeObjective !== null && getObjectiveKey(activeObjective) === key;

              return (
                <View key={key}>
                  {idx > 0 ? <View style={styles.expandedSeparator} /> : null}
                  <View style={styles.expandedRow}>
                    <View
                      style={[
                        styles.expandedLeadDot,
                        completed && styles.expandedLeadDotCompleted,
                        isActive && styles.expandedLeadDotActive,
                      ]}
                    />
                    <CustomText
                      text={obj.objective}
                      size="xs"
                      weight="medium"
                      useTranslationText={false}
                      style={[
                        styles.expandedObjectiveText,
                        completed && styles.expandedObjectiveTextCompleted,
                        isActive && styles.expandedObjectiveTextActive,
                      ]}
                    />
                  </View>
                </View>
              );
            })}
          </MotiView>
        ) : null}
      </AnimatePresence>

      <View
        onLayout={(e: LayoutChangeEvent) => setBarHeight(e.nativeEvent.layout.height)}
        style={[styles.bar, isExpanded && styles.barExpanded, Platform.OS === "web" && barHovered && styles.barHovered]}
        role="progressbar"
        aria-label="Activity progress"
        aria-valuemin={0}
        aria-valuemax={objectives.length}
        aria-valuenow={completedCount}
        accessibilityRole="progressbar"
        accessibilityLabel="Activity progress"
        accessibilityValue={{ min: 0, max: objectives.length, now: completedCount }}
        {...(Platform.OS === "web"
          ? {
              onPointerEnter: () => setBarHovered(true),
              onPointerLeave: () => setBarHovered(false),
            }
          : {})}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t("text.objectives_progress_bar_accessibility_label")}
          accessibilityHint={t("text.objectives_progress_bar_accessibility_hint")}
          accessibilityState={{ expanded: isExpanded }}
          onPress={() => setIsExpanded((v) => !v)}
          style={[styles.objectivesStrip, Platform.OS === "web" && styles.objectivesStripPressable]}>
          <MotiView
            key={allComplete ? "all-done" : getObjectiveKey(activeObjective ?? objectives[0])}
            from={{ scale: Platform.OS === "web" ? 1.22 : 1 }}
            animate={{ scale: 1 }}
            transition={{
              type: "timing",
              duration: 200,
              easing: Easing.out(Easing.cubic),
            }}>
            <View
              style={[styles.leadDot, { backgroundColor: allComplete ? colors.success[500] : colors.brand[500] }]}
            />
          </MotiView>

          <View style={styles.labelContainer}>
            {activeObjective ? (
              <CustomText
                text={activeObjective.objective}
                size="xs"
                weight="medium"
                style={styles.objectiveLabel}
                useTranslationText={false}
                numberOfLines={1}
                ellipsizeMode="tail"
              />
            ) : null}
          </View>

          <View style={styles.dotsRow}>
            {objectives.map((obj) => {
              const completed = !!obj.isCompleted;
              const key = getObjectiveKey(obj);
              const isActive = !completed && activeObjective !== null && getObjectiveKey(activeObjective) === key;
              const justCompleted = pulsingKeys.has(key);

              return (
                <View key={key} style={styles.dotWrapper}>
                  {justCompleted && Platform.OS === "web" ? (
                    <MotiView
                      pointerEvents="none"
                      style={styles.pulseRing}
                      from={{ scale: 1, opacity: 0.5 }}
                      animate={{ scale: 2, opacity: 0 }}
                      transition={{
                        type: "timing",
                        duration: 280,
                        easing: Easing.out(Easing.quad),
                      }}
                    />
                  ) : null}
                  <MotiView
                    animate={justCompleted ? { scale: [1, 1.28, 1] } : { scale: 1 }}
                    transition={{
                      type: "timing",
                      duration: 280,
                      easing: Easing.out(Easing.cubic),
                    }}>
                    <View
                      style={[
                        styles.objectiveDot,
                        completed && styles.objectiveDotCompleted,
                        isActive && styles.objectiveDotActive,
                      ]}
                    />
                  </MotiView>
                </View>
              );
            })}
          </View>

          <CustomText
            text={`${completedCount}/${objectives.length}`}
            size="xs"
            weight="medium"
            style={styles.counter}
            useTranslationText={false}
          />
        </Pressable>

        {!allComplete && (
          <AxioButton
            label={t("text.objectives_progress_bar_move_on")}
            variant="secondary"
            size="sm"
            useTranslationText={false}
            rightIcon={<ChevronRight size={12} color={colors.grey[700]} />}
            onPress={onMoveOn}
            disabled={isStreaming || hasPendingQuestion}
            style={styles.moveOn}
            textStyle={styles.moveOnText}
            accessibilityHint={t("text.objectives_progress_bar_move_on_accessibility_hint")}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    maxWidth: 768,
    alignSelf: "center",
    marginBottom: spacing.sm,
    overflow: "visible",
  },
  expandedPanel: {
    position: "absolute",
    left: 0,
    right: 0,
    zIndex: 0,
    elevation: 2,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: colors.grey[200],
    borderTopLeftRadius: radii.lg,
    borderTopRightRadius: radii.lg,
    paddingHorizontal: spacing.sm,
  },
  expandedSeparator: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[100],
  },
  expandedRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    paddingVertical: spacing.sm,
  },
  expandedLeadDot: {
    width: spacing.xs,
    height: spacing.xs,
    borderRadius: spacing.xs / 2,
    backgroundColor: colors.grey[300],
  },
  expandedLeadDotCompleted: {
    backgroundColor: colors.success[500],
  },
  expandedLeadDotActive: {
    backgroundColor: colors.brand[500],
  },
  expandedObjectiveText: {
    flex: 1,
    color: colors.grey[400],
  },
  expandedObjectiveTextCompleted: {
    color: colors.grey[500],
  },
  expandedObjectiveTextActive: {
    color: colors.grey[900],
  },
  bar: {
    position: "relative",
    zIndex: 2,
    elevation: 10,
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderTopColor: colors.grey[200],
    borderBottomColor: colors.grey[200],
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.grey[50],
  },
  barExpanded: {
    backgroundColor: colors.white,
  },
  barHovered: {
    backgroundColor: colors.grey[100],
  },
  objectivesStrip: {
    flex: 1,
    minWidth: 0,
    alignSelf: "stretch",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
    paddingVertical: spacing.sm,
  },
  objectivesStripPressable: {
    ...(Platform.OS === "web" ? ({ cursor: "pointer" } as const) : {}),
  },
  leadDot: {
    width: spacing.xs,
    height: spacing.xs,
    borderRadius: spacing.xs / 2,
  },
  labelContainer: {
    flex: 1,
    minWidth: 0,
  },
  objectiveLabel: {
    color: colors.grey[700],
  },
  dotsRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  dotWrapper: {
    width: spacing.xxs,
    height: spacing.xxs,
    alignItems: "center",
    justifyContent: "center",
  },
  pulseRing: {
    position: "absolute",
    width: spacing.xxs,
    height: spacing.xxs,
    borderRadius: spacing.xxs / 2,
    borderWidth: 1.5,
    borderColor: colors.brand[400],
  },
  objectiveDot: {
    width: spacing.xxs,
    height: spacing.xxs,
    borderRadius: spacing.xxs / 2,
    backgroundColor: colors.grey[300],
  },
  objectiveDotCompleted: {
    backgroundColor: colors.success[500],
  },
  objectiveDotActive: {
    backgroundColor: colors.brand[500],
  },
  counter: {
    color: colors.grey[400],
    ...(Platform.OS === "web" ? ({ fontVariant: ["tabular-nums"] } as const) : {}),
  },
  moveOn: {
    paddingVertical: spacing.xxs,
    paddingHorizontal: spacing.xs,
    ...shadowStyles.sm,
  },
  moveOnText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "500",
  },
});

export default ObjectivesProgressBar;
